import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/services/LoggingService';
import { determineStandardAssetClass } from '@/utils/asset-class';
import { getFundDisplayName } from '@/utils/fundDisplayUtils';
import {
  getDisplayNameByAssetType,
  getTickerByAssetType,
  getCompanyNameByAssetType,
  type Fund,
  type FundsMap,
} from '../lib/portfolioTransforms';
import { getPortfolioById } from '@/services/portfolio/PortfolioService';
import { fetchAllAllocations } from '@/services/portfolio/PortfolioAllocationsService';

// These types mirror the DB shape used by the original usePortfolioData
export interface PortfolioData {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
  user_id: string;
  portfolio_size?: number;
  status: string;
  risk_level?: 'low' | 'medium' | 'high';
  target_return?: number;
  portfolio_allocations: PortfolioAllocation[];
  description?: string;
  portfolio_type?: string;
}

export interface PortfolioAllocation {
  id: string;
  portfolio_id: string;
  asset_id: string;
  asset_type: string;
  allocation_percentage: number;
  allocation_value?: number | null;
  is_value_locked?: boolean;
  currency_code?: string | null;
  created_at: string;
  updated_at: string;
  fund?: Fund; // Enriched fund info
}

interface RawPortfolioAllocation {
  id: string;
  portfolio_id: string;
  asset_id: string;
  asset_type?: string;
  allocation_percentage: number;
  currency_code?: string | null;
  created_at: string;
  updated_at: string;
}

export const usePortfolioAllocations = (
  portfolioId: string | undefined
): UseQueryResult<PortfolioData | null, Error> & { enrichedAllocations: PortfolioAllocation[] } => {
  const queryKey = useMemo(() => ['portfolio', portfolioId, 'allocations'], [portfolioId]);

  const query = useQuery<PortfolioData | null, Error>({
    queryKey,
    queryFn: async () => {
      try {
        if (import.meta.env.DEV) logger.debug('usePortfolioAllocations', 'Fetching portfolio data', { portfolioId });
        if (!portfolioId) {
          logger.warn('usePortfolioAllocations', 'No portfolioId provided');
          return null;
        }

        // Fetch portfolio row
        const portfolioData = await getPortfolioById(portfolioId);
        
        if (!portfolioData) {
          logger.warn('usePortfolioAllocations', 'No portfolio found with that ID', { portfolioId });
          throw new Error('PORTFOLIO_NOT_FOUND');
        }

        // Fetch portfolio allocations (all types)
        const rawAllocations = await fetchAllAllocations(portfolioId);
        const allocationsData = rawAllocations as RawPortfolioAllocation[] | null;

        // If no allocations, return portfolio shell
        if (!allocationsData || allocationsData.length === 0) {
          const result: PortfolioData = {
            id: portfolioData.id,
            name: portfolioData.name,
            created_at: portfolioData.created_at,
            updated_at: portfolioData.updated_at,
            user_id: portfolioData.user_id,
            status: portfolioData.status,
            portfolio_allocations: [],
            ...(typeof (portfolioData as any).portfolio_size !== 'undefined' ? { portfolio_size: (portfolioData as any).portfolio_size } : {}),
            ...(typeof (portfolioData as any).risk_level !== 'undefined' ? { risk_level: (portfolioData as any).risk_level as 'low' | 'medium' | 'high' } : {}),
            ...(typeof (portfolioData as any).target_return !== 'undefined' ? { target_return: (portfolioData as any).target_return } : {}),
            ...(typeof (portfolioData as any).portfolio_type !== 'undefined' ? { portfolio_type: (portfolioData as any).portfolio_type } : {}),
          };
          return result;
        }

        // Build list of investment-asset IDs to fetch richer names from holdings/assets tables
        const assetIds = (allocationsData || [])
          .filter((allocation) => allocation.asset_type === 'asset' && allocation.asset_id)
          .map((allocation) => allocation.asset_id as string);

        // Also collect multi-asset IDs (these are investment account UUIDs)
        const multiAssetIds = (allocationsData || [])
          .filter((allocation) => (allocation.asset_type || '').toLowerCase() === 'multi_asset' && allocation.asset_id)
          .map((allocation) => allocation.asset_id as string);

        // Materialize assetsMap from investment_holdings and investment_assets
        const assetsMap: Record<string, any> = {};
        if (assetIds.length > 0) {
          if (import.meta.env.DEV) logger.debug('usePortfolioAllocations', 'Fetching investment asset data', { assetIdsCount: assetIds.length });
          const [holdingsResult, assetsResult] = await Promise.all([
            supabase.from('investment_holdings').select('id, asset_name, asset_type, market_value').in('id', assetIds),
            supabase.from('investment_assets').select('id, asset_name, asset_type, market_value').in('id', assetIds),
          ]);

          const holdingsData = holdingsResult.data || [];
          const assetsData = assetsResult.data || [];
          if (holdingsResult.error) logger.warn('usePortfolioAllocations', 'Error fetching investment holdings', holdingsResult.error);
          if (assetsResult.error) logger.warn('usePortfolioAllocations', 'Error fetching investment assets', assetsResult.error);

          // Deterministic merge: assets then holdings (holdings take precedence last)
          [...assetsData, ...holdingsData].forEach((item) => {
            if (item?.id) assetsMap[item.id] = item;
          });
        }

        // Unique fund IDs
        const fundIds = (allocationsData || []).map((allocation) => allocation.asset_id || '').filter((id) => id !== '');
        const uniqueFundIds = [...new Set(fundIds)];

        // Pull from funds and funds_raw
        const [fundsResult, fundsRawResult] = await Promise.all([
          supabase.from('funds').select('id, fund_name, asset_class').in('id', uniqueFundIds),
          supabase.from('funds_raw').select('id, fund_name, sa_asset_class').in('id', uniqueFundIds),
        ]);

        const { data: fundsData, error: fundsError } = fundsResult;
        const { data: fundsRawData, error: fundsRawError } = fundsRawResult;

        // Stock, investment tables, and multi-asset accounts as additional sources
        const [
          { data: holdingsData, error: holdingsError },
          { data: assetsData, error: assetsError },
          { data: stocksData, error: stocksError },
          { data: accountsData, error: accountsError },
        ] = await Promise.all([
          supabase.from('investment_holdings').select('id, asset_name, symbol, asset_type').in('id', uniqueFundIds),
          supabase.from('investment_assets').select('id, asset_name, symbol, asset_type').in('id', uniqueFundIds),
          supabase.from('stock_fundamentals').select('id, name, code, exchange_name, exchange').in('id', uniqueFundIds),
          multiAssetIds.length > 0
            ? supabase.from('investment_accounts').select('id, account_name').in('id', multiAssetIds)
            : Promise.resolve({ data: [], error: null } as any),
        ]);

        if (fundsError) logger.warn('usePortfolioAllocations', 'Error fetching funds table data', fundsError);
        if (fundsRawError) logger.warn('usePortfolioAllocations', 'Error fetching funds_raw table data', fundsRawError);
        if (stocksError) logger.warn('usePortfolioAllocations', 'Error fetching stock data', stocksError);
        if (holdingsError) logger.warn('usePortfolioAllocations', 'Error fetching holdings data', holdingsError);
        if (assetsError) logger.warn('usePortfolioAllocations', 'Error fetching assets data', assetsError);
        if (accountsError) logger.warn('usePortfolioAllocations', 'Error fetching investment account data', accountsError);

        // Build unified funds map
        const fundsMap: FundsMap = {};
        if (fundsData) {
          fundsData.forEach((fund) => {
            fundsMap[fund.id] = { id: fund.id, fund_name: fund.fund_name, sa_asset_class: (fund as any).asset_class, kind: 'fund' } as any;
          });
        }
        if (fundsRawData) {
          fundsRawData.forEach((fund) => {
            if (fundsMap[fund.id]) {
              fundsMap[fund.id] = {
                ...fundsMap[fund.id],
                fund_name: fundsMap[fund.id].fund_name || fund.fund_name,
                sa_asset_class: fund.sa_asset_class,
                kind: (fundsMap[fund.id] as any).kind || 'fund',
              } as any;
            } else {
              fundsMap[fund.id] = { id: fund.id, fund_name: fund.fund_name, sa_asset_class: fund.sa_asset_class, kind: 'fund' } as any;
            }
          });
        }
        if (stocksData) {
          stocksData.forEach((stock) => {
            fundsMap[stock.id] = {
              id: stock.id,
              display_name: `${stock.name} (${stock.code})`,
              name: stock.name,
              fund_name: `${stock.name} (${stock.code})`,
              ticker: stock.code,
              company_name: stock.exchange_name || stock.exchange,
              sa_asset_class: 'equity',
              kind: 'stock',
            } as any;
          });
        }
        if (holdingsData) {
          holdingsData.forEach((holding) => {
            if (fundsMap[holding.id]) {
              fundsMap[holding.id] = {
                ...fundsMap[holding.id],
                fund_name: fundsMap[holding.id].fund_name || holding.asset_name || holding.symbol,
                display_name: (fundsMap[holding.id] as any).display_name || holding.asset_name || holding.symbol,
                name: (fundsMap[holding.id] as any).name || holding.asset_name || holding.symbol,
                ticker: (fundsMap[holding.id] as any).ticker || holding.symbol,
                sa_asset_class: (fundsMap[holding.id] as any).sa_asset_class || (holding as any).asset_type || 'unclassified',
                kind: (fundsMap[holding.id] as any).kind || 'asset',
              } as any;
            } else {
              fundsMap[holding.id] = {
                id: holding.id,
                fund_name: holding.asset_name || holding.symbol || `Asset ${holding.id.substring(0, 6)}`,
                display_name: holding.asset_name || holding.symbol || `Asset ${holding.id.substring(0, 6)}`,
                name: holding.asset_name || holding.symbol || `Asset ${holding.id.substring(0, 6)}`,
                ticker: holding.symbol,
                sa_asset_class: (holding as any).asset_type || 'unclassified',
                kind: 'asset',
              } as Fund;
            }
          });
        }
        if (assetsData) {
          assetsData.forEach((asset) => {
            if (!fundsMap[asset.id]) {
              fundsMap[asset.id] = {
                id: asset.id,
                fund_name: asset.asset_name || asset.symbol || `Asset ${asset.id.substring(0, 6)}`,
                display_name: asset.asset_name || asset.symbol || `Asset ${asset.id.substring(0, 6)}`,
                name: asset.asset_name || asset.symbol || `Asset ${asset.id.substring(0, 6)}`,
                ticker: asset.symbol,
                sa_asset_class: (asset as any).asset_type || 'unclassified',
                kind: 'asset',
              } as Fund;
            } else {
              // ensure kind is at least 'asset' when enriching
              (fundsMap[asset.id] as any).kind = (fundsMap[asset.id] as any).kind || 'asset';
            }
          });
        }

        // Map multi-asset account names to fundsMap so they display nicely
        if (accountsData && Array.isArray(accountsData)) {
          accountsData.forEach((acct: any) => {
            const display = acct.account_name || 'Multi-Asset Account';
            fundsMap[acct.id] = {
              id: acct.id,
              fund_name: display,
              display_name: display,
              name: display,
              sa_asset_class: 'multi_asset',
              kind: 'account',
            } as Fund;
          });
        }

        // Build enriched allocations
        const enrichedAllocations: PortfolioAllocation[] = (allocationsData || []).map((allocation) => {
          const fundId = allocation.asset_id || '';
          const fundInfo = fundId ? fundsMap[fundId] : null;

          if (fundId && !fundInfo) {
            logger.warn('usePortfolioAllocations', 'No fund data found for allocation', { fundId, allocationId: allocation.id });
          }

          const normalizedAssetType = determineStandardAssetClass((fundInfo as any)?.sa_asset_class, allocation.asset_type);

          const result: PortfolioAllocation = {
            id: allocation.id,
            portfolio_id: allocation.portfolio_id,
            asset_id: fundId,
            asset_type: normalizedAssetType || 'unclassified',
            allocation_percentage: allocation.allocation_percentage,
            allocation_value: (allocation as any).allocation_value ?? null,
            is_value_locked: (allocation as any).is_value_locked ?? false,
            currency_code: (allocation as any).currency_code ?? null,
            created_at: allocation.created_at,
            updated_at: allocation.updated_at,
            fund:
              fundInfo ||
              (() => {
                const placeholderFund: Fund = {
                  id: fundId,
                  fund_name: getDisplayNameByAssetType(allocation.asset_type, fundId, assetsMap),
                  display_name: getDisplayNameByAssetType(allocation.asset_type, fundId, assetsMap),
                  name: getDisplayNameByAssetType(allocation.asset_type, fundId, assetsMap),
                  ticker: getTickerByAssetType(allocation.asset_type),
                  company_name: getCompanyNameByAssetType(allocation.asset_type),
                  sa_asset_class: normalizedAssetType || 'unclassified',
                };
                const displayName = getFundDisplayName(placeholderFund);
                return { ...placeholderFund, fund_name: displayName, display_name: displayName, name: displayName };
              })(),
          };
          return result;
        });

        const portfolio_allocations = [...enrichedAllocations];
        const result: PortfolioData = {
          id: portfolioData.id,
          name: portfolioData.name,
          created_at: portfolioData.created_at,
          updated_at: portfolioData.updated_at,
          user_id: portfolioData.user_id,
          status: portfolioData.status,
          portfolio_allocations,
          ...(typeof (portfolioData as any).portfolio_size !== 'undefined' ? { portfolio_size: (portfolioData as any).portfolio_size } : {}),
          ...(typeof (portfolioData as any).risk_level !== 'undefined' ? { risk_level: (portfolioData as any).risk_level as 'low' | 'medium' | 'high' } : {}),
          ...(typeof (portfolioData as any).target_return !== 'undefined' ? { target_return: (portfolioData as any).target_return } : {}),
          ...(typeof (portfolioData as any).description !== 'undefined' ? { description: (portfolioData as any).description } : {}),
          ...(typeof (portfolioData as any).portfolio_type !== 'undefined' ? { portfolio_type: (portfolioData as any).portfolio_type } : {}),
        };

        return result;
      } catch (error: any) {
        console.error('Error in portfolio allocations query:', error);
        toast.error('Failed to load portfolio data');
        throw error;
      }
    },
    staleTime: 30000,
    gcTime: 300000,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    retry: (failureCount, error) => (error.message === 'PORTFOLIO_NOT_FOUND' ? false : failureCount < 3),
  });

  const enrichedAllocations = useMemo(() => query.data?.portfolio_allocations ?? [], [query.data]);

  return Object.assign(query, { enrichedAllocations });
};
